import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import useDefaultCRUDHandlers from "../hooks/useDefaultCRUDHandlers";
import { IDeletePartResponse, IPartImageMpnAnalysisResponse, IProcessMpnImageResponse } from "../models/Part";

export default function usePartMutations() {
  const { get, post, put, patch, del, postDocs } = useApiHelper();
  const queryClient = useQueryClient();
  const { deleteHandlers, saveHandlers } = useDefaultCRUDHandlers("Part");
  const staleTime = 1000 * 60; //1 minutes

  const createParts = useMutation(
    (partData: any) => post(`/part`, partData),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["job"]);
        queryClient.invalidateQueries(["jobs"]);
        queryClient.invalidateQueries(["lead"]);
        queryClient.invalidateQueries(["vehicles"]);
        queryClient.invalidateQueries(["parts"]);

        // Invalidate specific vehicle and parts queries if vehicleId is available
        if (variables.vehicleId) {
          queryClient.invalidateQueries(["vehicles", variables.vehicleId]);
          queryClient.invalidateQueries(["parts", variables.vehicleId]);
        }

        // Also invalidate any queries that might contain vehicle data
        queryClient.invalidateQueries(["vehicle"]);

        // Force refetch of vehicle data to ensure immediate updates
        if (variables.vehicleId) {
          queryClient.refetchQueries(["vehicles", variables.vehicleId]);
        }
      },
    }
  );

  const markDamaged = useMutation(
    (partData: any) => patch(`/part/${partData.id}`, partData),
    {
      onSuccess: (_, partData) => {
        queryClient.invalidateQueries(["job"]);
        queryClient.invalidateQueries(["parts"]);

        // Also invalidate vehicle queries since part status affects vehicle data
        queryClient.invalidateQueries(["vehicles"]);

        // If we have the vehicle ID from the part, specifically invalidate and refetch that vehicle
        if (partData.vehicleId) {
          queryClient.invalidateQueries(["vehicles", partData.vehicleId]);
          queryClient.refetchQueries(["vehicles", partData.vehicleId]);
        } else {
          // Force refetch of all vehicle queries to ensure immediate updates
          queryClient.refetchQueries(["vehicles"]);
        }
      },
    }
  );

  const deletePart = useMutation(
    (partId: string) => del<IDeletePartResponse>(`/part/${partId}`, { deletedReason: 0 }),
    {
      onMutate: async (deletedId: string) => {
        // Cancel any outgoing refetches so they don't overwrite our optimistic update.
        await queryClient.cancelQueries(["pagedTable"]);

        // Get all queries with keys starting with "pagedTable".
        const previousQueries = queryClient.getQueriesData(["pagedTable"]);

        // Optimistically update all relevant pagedTable queries.
        previousQueries.forEach(([queryKey, oldData]) => {
          queryClient.setQueryData(queryKey, (currentData: any) => {
            if (!currentData) return currentData;
            return {
              ...currentData,
              data: {
                ...currentData.data,
                rows: currentData.data.rows.filter((item: any) => item.id !== deletedId),
              },
            };
          });
        });

        // Return context in case we need to roll back.
        return { previousQueries };
      },
      onError: (err, deletedId, context) => {
        // Roll back to the previous state if mutation fails.
        context?.previousQueries.forEach(([queryKey, oldData]) => {
          queryClient.setQueryData(queryKey, oldData);
        });
        deleteHandlers.onError();
      },
      onSuccess: () => {
        // Optionally invalidate queries to refetch fresh data.
        queryClient.invalidateQueries(["job"]);
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const restorePart = useMutation(
    (partId: string) => post(`/part/${partId}/restore`, null),
    {
      onSuccess: (_, partId) => {
        queryClient.invalidateQueries(["job"]);
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["parts", partId]);
        queryClient.invalidateQueries(["partDetails", partId]);
        queryClient.invalidateQueries(["pagedTable"]);
        queryClient.invalidateQueries(["vehicles"]);
      },
    }
  );

  const returnPart = useMutation(
    (partId: string) => patch(`/part/return/${partId}`, {}),
    {
      onSuccess: (_, partId) => {
        queryClient.invalidateQueries(["job"]);
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["parts", partId]);
        queryClient.invalidateQueries(["partDetails", partId]);
        queryClient.invalidateQueries(["pagedTable"]);
        queryClient.invalidateQueries(["vehicles"]);
      },
    }
  );

  const patchPart = useMutation(
    (partData: any) => patch(`/part/${partData.id}`, partData),
    {
      onMutate: async (partData: any) => {
        await queryClient.cancelQueries(["pagedTable"]);
        await queryClient.cancelQueries(["parts", partData.id]);
        await queryClient.cancelQueries(["partDetails", partData.id]);

        const previousPagedQueries = queryClient.getQueriesData(["pagedTable"]);
        const previousPart = queryClient.getQueryData(["parts", partData.id]);
        const previousPartDetails = queryClient.getQueryData(["partDetails", partData.id]);

        previousPagedQueries.forEach(([queryKey]) => {
          queryClient.setQueryData(queryKey, (currentData: any) => {
            if (!currentData?.data?.rows) return currentData;

            return {
              ...currentData,
              data: {
                ...currentData.data,
                rows: currentData.data.rows.map((row: any) =>
                  row.id === partData.id ? { ...row, ...partData } : row
                ),
              },
            };
          });
        });

        queryClient.setQueryData(["parts", partData.id], (currentData: any) => {
          if (!currentData?.data) return currentData;
          return {
            ...currentData,
            data: {
              ...currentData.data,
              ...partData,
            },
          };
        });

        queryClient.setQueryData(["partDetails", partData.id], (currentData: any) => {
          if (!currentData?.data) return currentData;
          return {
            ...currentData,
            data: {
              ...currentData.data,
              ...partData,
            },
          };
        });

        return { previousPagedQueries, previousPart, previousPartDetails };
      },
      onError: (_err, partData, context: any) => {
        context?.previousPagedQueries?.forEach(([queryKey, oldData]: any) => {
          queryClient.setQueryData(queryKey, oldData);
        });
        if (context?.previousPart !== undefined) {
          queryClient.setQueryData(["parts", partData.id], context.previousPart);
        }
        if (context?.previousPartDetails !== undefined) {
          queryClient.setQueryData(["partDetails", partData.id], context.previousPartDetails);
        }
      },
      onSuccess: (_, partData) => {
        queryClient.invalidateQueries(["job"]);
        queryClient.invalidateQueries(["parts", partData.id]);
        queryClient.invalidateQueries(["partDetails", partData.id]);
        queryClient.invalidateQueries(["pagedTable"]);

        // Also invalidate vehicle queries since part changes affect vehicle data
        queryClient.invalidateQueries(["vehicles"]);

        // If we have the vehicle ID from the part, specifically invalidate and refetch that vehicle
        if (partData.vehicleId) {
          queryClient.invalidateQueries(["vehicles", partData.vehicleId]);
          queryClient.refetchQueries(["vehicles", partData.vehicleId]);
        } else {
          // Force refetch of all vehicle queries to ensure immediate updates
          queryClient.refetchQueries(["vehicles"]);
        }
      },
    }
  );

  const bulkPatchPart = useMutation(
    (partData: any) => patch(`/part/bulk`, partData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["job"]);
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["pagedTable"]);

        // Also invalidate vehicle queries since part changes affect vehicle data
        queryClient.invalidateQueries(["vehicles"]);
      },
    }
  );

  const autoReorderImages = useMutation(
    (ids: any) => post(`/part/images/autoOrder`, ids),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["job"]);
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["pagedTable"]);

        // Also invalidate vehicle queries since part changes affect vehicle data
        queryClient.invalidateQueries(["vehicles"]);
      },
    }
  );

  const createOffer = useMutation(
    (partId: string) => post(`/part/offers/default/${partId}`, null),
    {
      onSuccess: (_, partId) => {
        queryClient.invalidateQueries(["parts", partId]);
        queryClient.invalidateQueries(["parts", "multiple"]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const bulkUpdateOfferPrice = useMutation(
    (body: any) => put(`/part/offers/price/bulk`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["job"]);
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["pagedTable"]);

        // Also invalidate vehicle queries since part changes affect vehicle data
        queryClient.invalidateQueries(["vehicles"]);
      },
    }
  );

  const bulkUpdateOffer = useMutation(
    (body: any) => put(`/part/offers/bulk`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["job"]);
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["pagedTable"]);
        queryClient.invalidateQueries(["vehicles"]);
      },
    }
  );

  const partQc = useMutation(
    (data: any) => post(`/part/qc/${data.partId}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["job"]);
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["pagedTable"]);

        // Also invalidate vehicle queries since part changes affect vehicle data
        queryClient.invalidateQueries(["vehicles"]);

        saveHandlers.onSuccess();
      },
    }
  );

  const bulkWithdraw = useMutation(
    (data: any) => post(`/part/offers/withdraw/bulk`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["job"]);
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["pagedTable"]);
        queryClient.invalidateQueries(["vehicles"]);
      },
    }
  );

  const scrapOutPart = useMutation(
    (partId: string) => del<IDeletePartResponse>(`/part/${partId}`, { deletedReason: 1 }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["job"]);
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const getMpnImageAnalysis = useMutation(
    (analysisId: string) => get<IPartImageMpnAnalysisResponse>(`/part/images/mpn-analysis/${analysisId}`)
  );

  const processMpnImage = useMutation(
    (imageId: string) => post<null, IProcessMpnImageResponse>(`/part/images/${imageId}/process-mpn`, null)
  );

  return {
    createParts,
    markDamaged,
    deletePart,
    scrapOutPart,
    restorePart,
    returnPart,
    patchPart,
    bulkPatchPart,
    autoReorderImages,
    createOffer,
    bulkUpdateOfferPrice,
    bulkUpdateOffer,
    partQc,
    bulkWithdraw,
    getMpnImageAnalysis,
    processMpnImage
  };
}
